import { POI_DISPLAY_TYPE } from '@/enums/poi';
import type { BuildingCategory, BuildingInStoreInfo, FloorInfo } from '@/services/dto';

const optionAllCategory: BuildingCategory = {
    clientCode: 'all',
    name: {
        ko: 'common.text.all_category',
        en: 'common.text.all_category',
        ja: 'common.text.all_category',
        'zh-CN': 'common.text.all_category',
        'zh-TW': 'common.text.all_category',
    },
    id: '',
    parentClientCode: '',
    indentLevel: 0,
    categoryCode: '',
    parentCategoryCode: '',
};
import categoryTypeUtils, { getCategoryTypeMenu, resolveCategoryTypePoiContents } from '@/utils/categoryType';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useConfigStore } from './configStore';
import type { TFunction } from 'i18next';
import { CATEGORY_TYPE_SEARCH_MODE } from '@/enums/category-type';
import type { CategoryTypeState, CategoryTypeActions, CategoryTypeFilterConfig } from '@/stores/types/categoryTypeStore';
import type { PoiContentSlide } from '@/types/shop';

interface CategoryTypeStore extends CategoryTypeState, CategoryTypeActions {}

export const useCategoryTypeStore = create<CategoryTypeStore>()(
    devtools(
        (set, get) => ({
            categoryTypeContents: [],
            rawCategoryTypeContents: [],
            menuId: null,
            initializedMenuId: null,
            searchText: '',
            selectedFloor: {
                id: 'all',
                label: '',
                value: '',
                data: null,
            },
            selectedBuildingId: 'all',
            selectedCategory: null,
            t: null,
            currentMode: CATEGORY_TYPE_SEARCH_MODE.SEARCH_BY_TEXT,
            keyboardLanguage: 'ko',

            setMenuId: (menuId: string | null) => {
                const prev = get().menuId;
                if (prev === menuId) return;
                set({ menuId, categoryTypeContents: [], rawCategoryTypeContents: [] });
            },

            setKeyboardLanguage: (language: 'ko' | 'en') => {
                const prev = get().keyboardLanguage;
                if (prev === language) return;
                set({ keyboardLanguage: language });
            },

            setCurrentMode: (mode: CATEGORY_TYPE_SEARCH_MODE) => {
                const prev = get().currentMode;
                if (prev === mode) return;
                set({ currentMode: mode });
            },

            setSearchText: (text: string) => {
                const prev = get().searchText;
                if (prev === text) return;
                set({ searchText: text });
            },

            setSelectedFloor: (floor: { id: string; label: string; value: string; data: FloorInfo | null | string[] } | null) => {
                const prev = get().selectedFloor;
                if (prev?.id === floor?.id) return;
                set({ selectedFloor: floor });
            },

            setSelectedBuildingId: (buildingId: string | null) => {
                const prev = get().selectedBuildingId;
                if (prev === buildingId) return;
                set({ selectedBuildingId: buildingId });
            },

            setSelectedCategory: (category: BuildingCategory) => {
                const prev = get().selectedCategory;
                if (prev?.clientCode === category?.clientCode) return;
                set({ selectedCategory: category });
            },

            setTranslationFunction: (t: TFunction<'translation', undefined>) => {
                set({ t });
            },

            resetFilters: () => {
                const state = get();
                const { t } = state;

                const { config } = useConfigStore.getState();
                const { server } = config;

                const allFloorText = t ? t('category_type.search.text.content.all_floor') : 'All Floors';
                const isMultipleBuildings = server?.store?.buildings && server?.store?.buildings?.length > 1;
                const newBuildingId = isMultipleBuildings ? 'all' : (server?.store?.buildings?.[0]?.id ?? 'all');

                set({
                    searchText: '',
                    selectedFloor: {
                        id: 'all',
                        label: allFloorText,
                        value: 'all',
                        data: null,
                    },
                    selectedBuildingId: newBuildingId,
                    selectedCategory: optionAllCategory,
                });
            },

            updateCategoryTypeContents: (searchMode: CATEGORY_TYPE_SEARCH_MODE): PoiContentSlide[] => {
                const state = get();
                const { selectedFloor, selectedBuildingId, selectedCategory, searchText, keyboardLanguage, menuId } = state;
                const { config } = useConfigStore.getState();
                const {
                    ui: { language, mainLanguage },
                } = config;

                const allBuildings = config.server?.store?.buildings ?? [];
                if (!allBuildings.length) {
                    if (state.categoryTypeContents?.length > 0) {
                        set({ categoryTypeContents: [] });
                    }
                    return [];
                }

                const menu = getCategoryTypeMenu(config.server?.buildingMenus, language, mainLanguage, menuId);
                const pois = resolveCategoryTypePoiContents(menu, selectedBuildingId, searchMode);

                const rawCategoryTypeContents = categoryTypeUtils.convertPoiContentToSlide(language, allBuildings, pois.length ? pois : undefined, searchText);

                const menuCategoryForFilter = menu?.selectedCategories?.find((c) => c.clientCode === selectedCategory?.clientCode);
                const categoryCodesForFilter: string[] = menuCategoryForFilter?.mergeCategoryCode ?? [];

                const contentsPropertyManagement = config.server?.store?.contentsPropertyManagement ?? [];
                // const keywordSearchEnabled = contentsPropertyManagement.some(
                //     (item) => item.display_type === POI_DISPLAY_TYPE.KEY_WORDS && item.visible !== false
                // );

                const filterConfig: CategoryTypeFilterConfig = {
                    language,
                    selectedBuildingId,
                    selectedFloor,
                    selectedCategory,
                    searchText,
                    buildings: config.server?.store?.buildings,
                    categoryCodesForFilter,
                    // keywordSearchEnabled,
                };

                if (!rawCategoryTypeContents?.length) {
                    if (state.categoryTypeContents?.length > 0) {
                        set({ categoryTypeContents: [] });
                    }
                    return [];
                }

                const contentFilter = categoryTypeUtils.createCategoryTypeFilter(searchMode, filterConfig, keyboardLanguage);
                const filteredContents = rawCategoryTypeContents.filter(contentFilter);

                if (filteredContents?.length === 0) {
                    if (state.categoryTypeContents?.length > 0) {
                        set({ categoryTypeContents: [] });
                    }
                    return [];
                }
                const getPriority = (char: string) => {
                    if (!char) return 4;
                    if (/[ㄱ-ㅎㅏ-ㅣ가-힣]/.test(char)) return 1;
                    if (/[a-zA-Z]/.test(char)) return 2;
                    if (/[0-9]/.test(char)) return 3;
                    return 4;
                };

                const sortedContents = filteredContents.sort((a, b) => {
                                        const nameA = (a?.data?.name?.[language] || '').trim();
                    const nameB = (b?.data?.name?.[language] || '').trim();

                    const priorityA = getPriority(nameA.charAt(0));
                    const priorityB = getPriority(nameB.charAt(0));

                    if (priorityA !== priorityB) {
                        return priorityA - priorityB;
                    }

                    return nameA.localeCompare(nameB, language, {
                        numeric: true,
                        sensitivity: 'base',
                        ignorePunctuation: true,
                    });
                });

                set({ categoryTypeContents: sortedContents });

                return sortedContents;
            },

            initialize: (t: TFunction<'translation', undefined>) => {
                const state = get();
                if (state.t === t && state.initializedMenuId === state.menuId && state.rawCategoryTypeContents?.length > 0) {
                    return;
                }

                const { config } = useConfigStore.getState();
                const {
                    ui: { language, mainLanguage },
                    server,
                } = config;
                const { menuId } = state;

                const allBuildings = server?.store?.buildings ?? [];
                const selectedBuildingId = allBuildings.length === 1 ? allBuildings[0].id : 'all';
                const menu = getCategoryTypeMenu(server?.buildingMenus, language, mainLanguage, menuId);
                const pois = resolveCategoryTypePoiContents(menu, selectedBuildingId, CATEGORY_TYPE_SEARCH_MODE.SEARCH_BY_TEXT);
                const rawCategoryTypeContents = categoryTypeUtils.convertPoiContentToSlide(language, allBuildings, pois.length ? pois : undefined);
                const updates: Partial<CategoryTypeState> = {
                    t,
                    rawCategoryTypeContents,
                    initializedMenuId: menuId,
                    currentMode: CATEGORY_TYPE_SEARCH_MODE.SEARCH_BY_TEXT,
                    selectedBuildingId,
                    selectedCategory: optionAllCategory,
                };

                set(updates);
            },
        }),
        {
            name: 'category-type-store',
        }
    )
);

let ctPreviousBuildings: BuildingInStoreInfo[] | undefined = undefined;
let ctPreviousLanguage: string = '';
let ctPreviousBuildingMenus: Record<string, unknown> | undefined = undefined;

useConfigStore.subscribe(() => {
    const { config } = useConfigStore.getState();
    const currentBuildings = config.server?.store?.buildings;
    const currentLanguage = config.ui?.language;
    const currentBuildingMenus = config.server?.buildingMenus as Record<string, unknown> | undefined;

    if (currentBuildings !== ctPreviousBuildings || currentLanguage !== ctPreviousLanguage || currentBuildingMenus !== ctPreviousBuildingMenus) {
        ctPreviousBuildings = currentBuildings;
        ctPreviousLanguage = currentLanguage;
        ctPreviousBuildingMenus = currentBuildingMenus;

        const { initialize, t } = useCategoryTypeStore.getState();
        if (t) {
            useCategoryTypeStore.setState({ initializedMenuId: null });
            initialize(t);
        }
    }
});
